import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale } from "@/i18n/context";
import { switchLocalePath } from "@/i18n/routes";
import type { Locale } from "@/i18n/types";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import { CircleFlag } from "react-circle-flags";
import { useLocation, useNavigate } from "react-router-dom";

const locales: { code: Locale; label: string; countryCode: string }[] = [
  { code: "sl", label: "Slovenščina", countryCode: "si" },
  { code: "en", label: "English", countryCode: "gb" },
  { code: "de", label: "Deutsch", countryCode: "de" },
  { code: "ru", label: "Русский", countryCode: "ru" },
];

function LocaleFlag({
  countryCode,
  size = 20,
  className,
}: {
  countryCode: string;
  size?: number;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 overflow-hidden rounded-full",
        className
      )}
      style={{ width: size, height: size }}
    >
      <CircleFlag
        countryCode={countryCode}
        height={size}
        width={size}
        className="block h-full w-full object-cover"
        aria-hidden
      />
    </span>
  );
}

export function LanguageSwitcher() {
  const { locale, t } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();

  const current = locales.find((item) => item.code === locale) ?? locales[0];

  const handleSwitch = (code: Locale) => {
    if (code === locale) return;
    navigate(switchLocalePath(location.pathname, code));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1 px-1.5"
          aria-label={`${t.common.language}: ${current.label}`}
        >
          <LocaleFlag countryCode={current.countryCode} size={18} />
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {locales.map(({ code, label, countryCode }) => (
          <DropdownMenuItem
            key={code}
            className="gap-2.5"
            onClick={() => handleSwitch(code)}
          >
            <LocaleFlag countryCode={countryCode} size={18} />
            <span className="flex-1">{label}</span>
            {locale === code ? (
              <Check className="h-4 w-4 text-primary" aria-hidden />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
